import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface Sermon {
  id: string;
  title: string;
  subtitle?: string;
  date?: string;
  speaker?: string;
  status?: string;
  series?: string;
  thumbnail_url?: string;
  video_url?: string;
  video_url_secondary?: string;
  notes?: string;
  is_live?: boolean;
  questions_enabled?: boolean;
  confessionCount?: number;
  series_id?: string | null;
  pointsCount?: number;
}

export const useSermons = () => {
  return useQuery<Sermon[]>({
    queryKey: ['sermons', 'v2'],
    queryFn: async () => {
      // 1. Fetch sermons with potential foreign key id
      const { data: sermons, error } = await supabase
        .from('sermons')
        .select(
          'id, title, subtitle, date, speaker, status, series_id, thumbnail_url, video_url, video_url_secondary, notes, confession_image_urls, is_live, questions_enabled'
        )
        .order('date', { ascending: false })
        .limit(200);
      if (error) throw error;

      const seriesIds: string[] = [];
      (sermons ?? []).forEach((s: any) => {
        if (s.series_id && !seriesIds.includes(s.series_id)) seriesIds.push(s.series_id);
      });

      let seriesMap: Record<string, string> = {};
      if (seriesIds.length) {
        const { data: seriesRows, error: seriesErr } = await supabase
          .from('sermon_series')
          .select('id, title')
          .in('id', seriesIds);
        if (seriesErr) throw seriesErr;
        seriesRows?.forEach((sr: any) => {
          seriesMap[sr.id] = sr.title;
        });
      }

      const transformed = (sermons ?? []).map((row: any) => ({
        id: row.id,
        title: row.title,
        subtitle: row.subtitle,
        date: row.date,
        speaker: row.speaker,
        status: row.status,
        series_id: row.series_id,
        series: row.series_id ? seriesMap[row.series_id] : undefined,
        thumbnail_url: row.thumbnail_url,
        video_url: row.video_url,
        video_url_secondary: row.video_url_secondary,
        notes: row.notes,
        is_live: Boolean(row.is_live),
        questions_enabled: Boolean(row.questions_enabled),
        confessionCount: Array.isArray(row.confession_image_urls) ? row.confession_image_urls.length : 0,
        pointsCount: 0,
      })) as Sermon[];

      // Fetch points (documents) counts in parallel
      if (transformed.length) {
        const sermonIds = transformed.map((s) => s.id);
        const { data: docs, error: docsErr } = await supabase
          .from('sermon_documents')
          .select('id, sermon_id')
          .in('sermon_id', sermonIds);
        if (docsErr) throw docsErr;
        const countMap: Record<string, number> = {};
        (docs ?? []).forEach((d: any) => {
          countMap[d.sermon_id] = (countMap[d.sermon_id] || 0) + 1;
        });
        transformed.forEach((t) => {
          t.pointsCount = countMap[t.id] || 0;
        });
      }

      return transformed;
    },
    staleTime: 5 * 60_000,
  });
};
